import React, { memo, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment-timezone';
import LoadingSpinner from '../../spinner/LoadingSpinner';
import warningIcon from '../../../assets/images/info-icon-gray.svg';
import unavailableIcon from '../../../assets/images/beach-access.svg';
import { getShiftsToRequest } from '../../../hooks/useShiftsForm';

import './FlexAssignmentConfirmationModal.scss';

const formatShiftTime = (structure, facilityTimezone) => {
	if (!structure || !structure.from_time || !structure.to_time) return '';
	const today = moment.tz(facilityTimezone).format('YYYY-MM-DD');
	const start = moment.tz(`${today} ${structure.from_time}`, 'YYYY-MM-DD HH:mm:ss', facilityTimezone);
	const end = moment.tz(`${today} ${structure.to_time}`, 'YYYY-MM-DD HH:mm:ss', facilityTimezone);
	if (end.isSameOrBefore(start)) end.add(1, 'day');
	return `${start.format('h:mm A')} – ${end.format('h:mm A')}`;
};

const formatStructureLabel = (structure) => {
	if (!structure) return '';
	if (structure.specialty) return `${structure.resource_type} - ${structure.specialty}`;
	return structure.resource_type;
};

const resolveUnitLabel = (group, units) => {
	if (group.unitName) return group.unitName;
	if (!group.unitId) return null;
	const match = (units || []).find((unit) => unit.id === group.unitId || unit.value === group.unitId);
	return match ? match.label || match.name || null : null;
};

const buildRows = (shiftsGroups, structuresById, units, facilityTimezone) => {
	const unitNameById = new Map();
	(shiftsGroups || []).forEach((group) => {
		if (group.unitId && group.unitName && !unitNameById.has(group.unitId)) {
			unitNameById.set(group.unitId, group.unitName);
		}
	});
	const shifts = getShiftsToRequest(shiftsGroups || [], true).sort((a, b) =>
		moment(a.start_date).diff(moment(b.start_date))
	);
	return shifts.map((shift, index) => {
		const dayMoment = facilityTimezone ? moment.tz(shift.start_date, facilityTimezone) : moment(shift.start_date);
		const unitLabel = resolveUnitLabel(
			{ unitId: shift.unit_id, unitName: unitNameById.get(shift.unit_id) || '' },
			units
		);
		return {
			key: `${shift.structure_id}|${shift.unit_id || 'none'}|${shift.start_date}|${index}`,
			structure: structuresById.get(shift.structure_id),
			unitLabel,
			dayMoment,
			isoDate: shift.start_date,
			slots: shift.slots
		};
	});
};

const FlexAssignmentConfirmationModal = ({
	title,
	professionalLabel,
	facilityLabel,
	shiftsGroups,
	shiftStructures,
	currentFacilityUnits,
	facilityTimezone,
	unavailableDates,
	showUnits,
	showShiftsRequested,
	onConfirm,
	onCancel,
	confirmDisabled,
	showLoadingSpinner
}) => {
	const structuresById = useMemo(() => {
		const map = new Map();
		(shiftStructures || []).forEach((structure) => map.set(structure.id, structure));
		return map;
	}, [shiftStructures]);

	const rows = useMemo(() => buildRows(shiftsGroups, structuresById, currentFacilityUnits, facilityTimezone), [
		shiftsGroups,
		structuresById,
		currentFacilityUnits,
		facilityTimezone
	]);

	const unavailableCount = unavailableDates ? unavailableDates.size : 0;

	return (
		<Modal
			show
			size="xl"
			centered
			scrollable
			onHide={onCancel}
			aria-labelledby="flex-confirmation-title"
			className="flex-assignment-confirmation-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title id="flex-confirmation-title">{title}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{showLoadingSpinner && (
					<div className="spinner-wrapper">
						<LoadingSpinner />
					</div>
				)}
				{unavailableCount > 0 && (
					<div className="flex-confirmation-banner">
						<img src={warningIcon} alt="" className="flex-confirmation-banner__icon" />
						<span>
							This professional is unavailable on <strong>{unavailableCount}</strong> of the selected dates. Are you
							sure you want to continue assigning this professional?
						</span>
					</div>
				)}
				{(professionalLabel || facilityLabel) && (
					<div className="flex-confirmation-meta">
						{professionalLabel && (
							<div className="flex-confirmation-meta-row">
								Professional: <strong>{professionalLabel}</strong>
							</div>
						)}
						{facilityLabel && (
							<div className="flex-confirmation-meta-row">
								Facility: <strong>{facilityLabel}</strong>
							</div>
						)}
					</div>
				)}
				<div className="flex-confirmation-table-wrap">
					<table className="flex-confirmation-table">
						<thead>
							<tr>
								<th>Shift type</th>
								<th>Date</th>
								<th>Shift time</th>
								{showUnits && <th>Unit</th>}
								{showShiftsRequested && <th>Shifts requested</th>}
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => {
								const isUnavailable = unavailableDates && unavailableDates.has(row.isoDate);
								return (
									<tr key={row.key}>
										<td>{formatStructureLabel(row.structure)}</td>
										<td>
											<span
												className={classNames('flex-confirmation-date', {
													'flex-confirmation-date--warning': isUnavailable
												})}
											>
												{' '}
												{isUnavailable && <img src={unavailableIcon} alt="" />}
												<strong>{row.dayMoment.format('ddd')}</strong>&nbsp;{row.dayMoment.format('MM/DD')}
												{isUnavailable && ' (Unavailable)'}
											</span>
										</td>
										<td className="flex-confirmation-shift-time">{formatShiftTime(row.structure, facilityTimezone)}</td>
										{showUnits && <td>{row.unitLabel || '-'}</td>}
										{showShiftsRequested && <td>{row.slots}</td>}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onCancel} disabled={showLoadingSpinner}>
					Go back
				</Button>
				<Button variant="primary" onClick={onConfirm} disabled={showLoadingSpinner || confirmDisabled}>
					Confirm
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default memo(FlexAssignmentConfirmationModal);
